import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { msalInstance, msalReady } from './auth/msalConfig';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AppShell } from './components/layout/AppShell';
import { ROUTES } from './constants/routes';
import { theme } from './styles/theme';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { CreateWorkOrder } from './pages/workorders/CreateWorkOrder';
import { SitesPage } from './pages/sites/SitesPage';
import { SiteDetailPage } from './pages/sites/SiteDetailPage';
import { LocationsManagePage } from './pages/sites/LocationsManagePage';
import { ProjectDetailPage } from './pages/project/ProjectDetailPage';
import { TestWorkflowPage } from './pages/TestWorkflow/TestWorkflowPage';
import { WorkOrdersTabPage } from './pages/WorkOrdersTab/WorkOrdersTabPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { ClientDetailPage } from './pages/clients/ClientDetailPage';
import { SamplesPage } from './pages/samples/SamplesPage';
import { LoginPage } from './pages/auth/LoginPage';
import { PendingAccessPage } from './pages/auth/PendingAccessPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { MoftakiBotPage } from './pages/moftakibot/MoftakiBotPage';
import { TestsListingPage } from './pages/tests/TestsListingPage';
import { MyTestsPage } from './pages/tests/MyTestsPage';
import { QuotesPage } from './pages/quotes/QuotesPage';
import { QuoteEditorPage } from './pages/quotes/editor/QuoteEditorPage';
import { PendingApprovalsPage } from './pages/quotes/PendingApprovalsPage';
import { PublicAcceptPage } from './pages/quotes/PublicAcceptPage';
import { PublicIntakePage } from './pages/intake/public/PublicIntakePage';
import { PublicIntakeStartPage } from './pages/intake/public/PublicIntakeStartPage';
import { IntakeRequestsPage } from './pages/intake/IntakeRequestsPage';
import { IntakeDetailPage } from './pages/intake/IntakeDetailPage';
import { MyFeedbackPage } from './pages/feedback/MyFeedbackPage';
import { FeedbackDetailPage } from './pages/feedback/FeedbackDetailPage';
import { ImpersonationProvider } from './hooks/useImpersonation';
import { UnsavedGuardProvider } from './hooks/useUnsavedGuard';
import { SchedulePage } from './pages/schedule/SchedulePage';
import { HowItWorksPage } from './pages/how-it-works/HowItWorksPage';

export const App = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    msalReady.then(() => setReady(true)).catch(console.error);
  }, []);

  if (!ready) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: '#3E4A3E' }} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider msalInstance={msalInstance}>
        <ImpersonationProvider>
        <UnsavedGuardProvider>
          <Routes>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.ACCESS_PENDING} element={<PendingAccessPage />} />
            <Route path={ROUTES.PUBLIC_QUOTE_ACCEPT} element={<PublicAcceptPage />} />
            <Route path={ROUTES.PUBLIC_INTAKE_START} element={<PublicIntakeStartPage />} />
            <Route path={ROUTES.PUBLIC_INTAKE} element={<PublicIntakePage />} />
            {/* Bulk print preview — auth-gated but rendered without AppShell chrome */}
            {/* Interactive "How It Works" experience (hub + chapters) — auth-gated, full-screen, no AppShell chrome */}
            <Route path={`${ROUTES.HOW_IT_WORKS}/*`} element={<ProtectedRoute><HowItWorksPage /></ProtectedRoute>} />
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
              <Route path={ROUTES.CLIENTS} element={<ClientsPage />} />
              <Route path={ROUTES.CLIENT_DETAIL} element={<ClientDetailPage />} />
              <Route path={ROUTES.SAMPLES} element={<SamplesPage />} />
              <Route path={ROUTES.SITES} element={<SitesPage />} />
              <Route path={ROUTES.SITE_DETAIL} element={<SiteDetailPage />} />
              <Route path={ROUTES.LOCATIONS_MANAGE} element={<LocationsManagePage />} />
              {/* Alias /locations → LocationsManagePage so direct URLs / bookmarks work (TASK-014) */}
              <Route path="/locations" element={<LocationsManagePage />} />
              <Route path={ROUTES.PROJECTS} element={<ProjectsPage />} />
              <Route path={ROUTES.WORK_ORDERS_TAB} element={<WorkOrdersTabPage />} />
              <Route path={ROUTES.TESTS} element={<TestsListingPage />} />
              <Route path={ROUTES.MY_TESTS} element={<MyTestsPage />} />
              <Route path={ROUTES.QUOTES} element={<QuotesPage />} />
              <Route path={ROUTES.QUOTE_NEW} element={<QuoteEditorPage />} />
              <Route path={ROUTES.PENDING_APPROVALS} element={<ProtectedRoute requiredRoles={['admin']}><PendingApprovalsPage /></ProtectedRoute>} />
              <Route path={ROUTES.INTAKE_REQUESTS} element={<IntakeRequestsPage />} />
              <Route path={ROUTES.INTAKE_DETAIL} element={<IntakeDetailPage />} />
              <Route path={ROUTES.QUOTE_EDITOR} element={<QuoteEditorPage />} />
              <Route path={ROUTES.LOCATIONS} element={<ProjectDetailPage />} />
              <Route path={ROUTES.WORK_ORDERS} element={<ProjectDetailPage />} />
              {/* TASK-008: Project Tests tab for bulk report generation */}
              <Route path="/projects/:projectId/tests" element={<ProjectDetailPage />} />
              <Route path={ROUTES.CREATE_WORK_ORDER} element={<CreateWorkOrder />} />
              <Route path={ROUTES.WORK_ORDER_DETAIL} element={<ProjectDetailPage />} />
              <Route path={ROUTES.TEST_PARAMETERS} element={<TestWorkflowPage />} />
              <Route path={ROUTES.MY_FEEDBACK} element={<MyFeedbackPage />} />
              <Route path={ROUTES.MY_FEEDBACK_DETAIL} element={<FeedbackDetailPage />} />
              <Route path={`${ROUTES.SCHEDULE}/*`} element={<SchedulePage />} />
              <Route path="/moftakibot" element={<MoftakiBotPage />} />
              <Route path="/moftakibot/:conversationId" element={<MoftakiBotPage />} />
              <Route path="/admin/feedback/:ticketKey" element={
                <ProtectedRoute requiredRoles={['admin']}><FeedbackDetailPage isAdmin /></ProtectedRoute>
              } />
              <Route path="/admin/*" element={
                <ProtectedRoute requiredRoles={['admin']}><AdminLayout /></ProtectedRoute>
              } />
            </Route>
          </Routes>
        </UnsavedGuardProvider>
        </ImpersonationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
